import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Box, Button, Heading, Text, InputFormField, Spinner } from '@indeed/ifl-components';
import { Helmet } from 'react-helmet';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobInfoHeader from '../components/JobInfoHeader';
import {
    useGetAutoApplyContactInfoFormDataQuery,
    useUpdateAutoApplyContactInfoMutation,
} from './__generated__/ContactInfoPage.generated';
import {
    SmartApplyContactInfoUpdate,
    updateSmartApplyContactInfoCache,
    buildCompleteContactInfoMutationInput,
} from '../../../lib/apollo/smartApplyStateCache';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import { AutoApplyAction } from '../autoApplyActions';
import { AutoApplyRumContext, logAutoApplyRumError } from '../autoApplyRumErrors';

/**
 * Contact Info page for Auto Apply flow.
 * Allows editing contact information for the current draft.
 *
 * ## Frontend availability classification
 *
 * | Failure                                          | `pageFailure` | Behavior                         |
 * | ------------------------------------------------ | ------------- | -------------------------------- |
 * | Missing draft or contact data                    | `true`        | Block the form                   |
 * | Save, missing mutation payload, or HTTP response | `true`        | Preserve the existing error path |
 * | Connectivity without a server response           | `true`        | Preserve the existing error path |
 * | Non-null response differs from submitted data    | `true`        | Preserve navigation              |
 *
 * Load failures are deduplicated by draft ID.
 *
 * Data source: SmartApplyApplicationState via GraphQL
 * - Reads contact info from Apollo cache (cache-first)
 * - Partial cache writes on field changes (immediate UI updates)
 * - Complete mutation on "Update" to prevent server data loss
 * - ITV (Intent-to-Verify) pattern for mutation validation
 *
 * Reference: integrations-apply/src/pages/smart-apply/contact-info/ContactInfoPage.tsx
 */
const ContactInfoPage: React.FC = () => {
    const navigate = useNavigate();
    const { draftId } = useParams<{ draftId: string }>();
    const location = useLocation();
    const apolloClient = useApolloClient();
    const eventLogger = useEventLogger();

    // SmartApplyApplicationState uses draftId as its globally unique node ID
    // (see SmartApplyApplicationState.id in the schema)

    // 1. Fetch contact info from SmartApplyApplicationState
    const { data, loading, error } = useGetAutoApplyContactInfoFormDataQuery({
        variables: { nodeId: draftId || '' },
        skip: !draftId,
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    // 2. Mutation for saving contact info with ITV pattern
    const [updateContactInfo, { loading: saving }] = useUpdateAutoApplyContactInfoMutation();

    // 3. Extract SmartApplyApplicationState from query response
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;
    const contactInfo = smartApplyState?.contactInfo;
    const reportedLoadFailureDraftId = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (
            !loading &&
            draftId &&
            (!smartApplyState || !contactInfo) &&
            reportedLoadFailureDraftId.current !== draftId
        ) {
            reportedLoadFailureDraftId.current = draftId;
            logAutoApplyRumError(
                error ?? new Error('Required contact information was missing'),
                AutoApplyRumContext.ContactInfoLoad,
            );
        }
    }, [contactInfo, draftId, error, loading, smartApplyState]);

    // 4. Handle field changes with direct Apollo cache writes
    const handleFieldChange = (field: keyof SmartApplyContactInfoUpdate, value: string) => {
        if (loading || !smartApplyState?.id) {
            console.warn('Cannot update cache: loading or missing SmartApplyApplicationState ID');
            return;
        }

        const updatedContactInfo = updateSmartApplyContactInfoCache(
            apolloClient.cache,
            smartApplyState.id,
            { [field]: value },
        );

        if (!updatedContactInfo) {
            console.error(`Failed to update ${field} in cache`);
        }
    };

    // 6. Handle Update - Complete mutation with ITV (Intent-to-Verify) pattern
    // TODO: Add field validation in a follow-up JIRA ticket
    const handleUpdate = async () => {
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: AutoApplyAction.Update,
            draftId: AvroOptional.string(draftId),
        });

        if (!smartApplyState?.id || !contactInfo) {
            console.error('Cannot save: missing SmartApplyApplicationState ID or contact info');
            return;
        }

        // CRITICAL: Build complete mutation input to prevent server data loss
        const completeMutationInput = buildCompleteContactInfoMutationInput(contactInfo, {
            firstName: contactInfo.firstName,
            lastName: contactInfo.lastName,
            email: contactInfo.email,
            phoneNumber: contactInfo.phoneNumber,
        });

        try {
            // 1. Send complete mutation (no `update` callback - ITV pattern)
            const result = await updateContactInfo({
                variables: {
                    input: { id: smartApplyState.id },
                    updateContactInfoInput: completeMutationInput,
                },
            });

            // 2. ITV check: Verify server response matches user intent
            const serverState = result.data?.smartApplyApplicationState?.updateContactInfo?.state;
            if (!serverState) {
                eventLogger.logReviewError?.({
                    currentPage: location.pathname,
                    errorType: { string: 'contact-info-itv-error' },
                    errorMessage: { string: 'No data returned from mutation' },
                    draftId: AvroOptional.string(draftId),
                    graphqlOperation: { string: 'UpdateAutoApplyContactInfo' },
                });
                throw new Error('No data returned from mutation');
            }

            // Compare the returned values without changing the established navigation behavior.
            // A mismatch is a page-level verification failure even when the save can continue.
            const intentFields = ['firstName', 'lastName', 'email', 'phoneNumber'] as const;
            const hasIntentMismatch = intentFields.some(
                (field) => serverState[field] !== completeMutationInput[field],
            );
            if (hasIntentMismatch) {
                eventLogger.logReviewError?.({
                    currentPage: location.pathname,
                    errorType: { string: 'contact-info-itv-error' },
                    errorMessage: {
                        string: 'Mutation response did not match submitted contact info',
                    },
                    draftId: AvroOptional.string(draftId),
                    graphqlOperation: { string: 'UpdateAutoApplyContactInfo' },
                });
                logAutoApplyRumError(
                    new Error('Mutation response did not match submitted contact info'),
                    AutoApplyRumContext.ContactInfoItv,
                );
            }

            // 3. Navigate back to review page after successful save
            // Use replace: true to remove edit page from browser history
            // so pressing back from review doesn't return to the edit page
            console.log('✅ Contact info saved successfully');
            void navigate(`/auto-apply/${draftId}/review${location.search}`, { replace: true });
        } catch (err) {
            console.error('Failed to save contact info:', err);
            const context =
                err instanceof Error && err.message === 'No data returned from mutation'
                    ? AutoApplyRumContext.ContactInfoItv
                    : AutoApplyRumContext.ContactInfoSave;
            logAutoApplyRumError(err, context);
            // TODO: Show user-friendly error dialog
        }
    };

    // Show loading state while fetching data
    if (loading) {
        return (
            <PageTransition pageKey={`contact-info-loading-${draftId}`}>
                <Helmet>
                    <title>Loading Contact Info - Indeed</title>
                </Helmet>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 8,
                        minBlockSize: '200px',
                    }}
                >
                    <Spinner title="Loading contact information..." />
                </Box>
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey={`contact-info-edit-${draftId}`}>
            <Helmet>
                <title>Edit Contact Info - Indeed</title>
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minBlockSize: '100vh',
                    backgroundColor: 'color.background.surface',
                }}
            >
                {/* Job Info Header */}
                <JobInfoHeader />

                {/* Main Content */}
                <Box
                    sx={{
                        flex: 1,
                        padding: '24px 16px',
                        maxInlineSize: '400px',
                        margin: '0 auto',
                        inlineSize: '100%',
                    }}
                >
                    {/* Header */}
                    <Heading level={1} sx={{ marginBlockEnd: 2 }}>
                        Add your contact information
                    </Heading>
                    <Text level={2} sx={{ color: 'color.text.secondary', marginBlockEnd: 4 }}>
                        We'll save any changes to your profile.
                    </Text>

                    {/* Form Fields */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {/* First Name */}
                        <InputFormField
                            label="First name"
                            inputProps={{
                                value: contactInfo?.firstName || '',
                                onChange: (e) => handleFieldChange('firstName', e.target.value),
                                placeholder: 'Enter first name',
                            }}
                        />

                        {/* Last Name */}
                        <InputFormField
                            label="Last name"
                            inputProps={{
                                value: contactInfo?.lastName || '',
                                onChange: (e) => handleFieldChange('lastName', e.target.value),
                                placeholder: 'Enter last name',
                            }}
                        />

                        {/* Email */}
                        <InputFormField
                            label="Email"
                            inputProps={{
                                type: 'email',
                                value: contactInfo?.email || '',
                                onChange: (e) => handleFieldChange('email', e.target.value),
                                placeholder: 'Enter email address',
                            }}
                        />

                        {/* Phone Number */}
                        <InputFormField
                            label="Phone number"
                            inputProps={{
                                type: 'tel',
                                value: contactInfo?.phoneNumber || '',
                                onChange: (e) => handleFieldChange('phoneNumber', e.target.value),
                                placeholder: '(000) 000-0000',
                            }}
                        />
                    </Box>

                    {/* Update Button */}
                    <Box sx={{ marginBlockStart: 5 }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleUpdate}
                            disabled={saving}
                            sx={{ inlineSize: '100%' }}
                        >
                            {saving ? 'Saving...' : 'Update'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ContactInfoPage;
